import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface AddChildLevelModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedNodeName: string;
  existingCodes: string[];
  onAdd: (code: string, name: string, notes: string) => void;
}

export function AddChildLevelModal({
  open,
  onOpenChange,
  selectedNodeName,
  existingCodes,
  onAdd,
}: AddChildLevelModalProps) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setCode('');
      setName('');
      setNotes('');
      setErrors({});
    }
  }, [open]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Code validation
    if (!code.trim()) {
      newErrors.code = 'This field is required';
    } else if (!/^[a-zA-Z0-9-]+$/.test(code)) {
      newErrors.code = 'Only alphanumeric characters and hyphens allowed';
    } else if (code.length > 20) {
      newErrors.code = 'Maximum 20 characters allowed';
    } else if (existingCodes.includes(code)) {
      newErrors.code = 'This code already exists';
    }

    // Name validation
    if (!name.trim()) {
      newErrors.name = 'This field is required';
    } else if (name.length > 100) {
      newErrors.name = 'Maximum 100 characters allowed';
    }

    // Notes validation
    if (notes.length > 500) {
      newErrors.notes = 'Maximum 500 characters allowed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onAdd(code.trim(), name.trim(), notes.trim());
    }
  };

  const handleBlur = (field: string) => {
    // Validate single field on blur
    const newErrors = { ...errors };
    
    if (field === 'code') {
      if (!code.trim()) {
        newErrors.code = 'This field is required';
      } else if (!/^[a-zA-Z0-9-]+$/.test(code)) {
        newErrors.code = 'Only alphanumeric characters and hyphens allowed';
      } else if (code.length > 20) {
        newErrors.code = 'Maximum 20 characters allowed';
      } else if (existingCodes.includes(code)) {
        newErrors.code = 'This code already exists';
      } else {
        delete newErrors.code;
      }
    } else if (field === 'name') {
      if (!name.trim()) {
        newErrors.name = 'This field is required';
      } else if (name.length > 100) {
        newErrors.name = 'Maximum 100 characters allowed';
      } else {
        delete newErrors.name;
      }
    } else if (field === 'notes') {
      if (notes.length > 500) {
        newErrors.notes = 'Maximum 500 characters allowed';
      } else {
        delete newErrors.notes;
      }
    }
    
    setErrors(newErrors);
  };

  const isValid = code.trim() && name.trim() && Object.keys(errors).length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Child Level</DialogTitle>
          <DialogDescription>
            Creating a child level below: <span className="font-medium text-foreground">{selectedNodeName}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="code">
              Code <span className="text-destructive">*</span>
            </Label>
            <Input
              id="code"
              placeholder="e.g., TASK-01"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onBlur={() => handleBlur('code')}
              className={errors.code ? 'border-destructive' : ''}
            />
            {errors.code && (
              <p className="text-sm text-destructive">{errors.code}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              placeholder="e.g., Testing Activities"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => handleBlur('name')}
              className={errors.name ? 'border-destructive' : ''}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Optional notes about this level..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={() => handleBlur('notes')}
              rows={3}
              className={errors.notes ? 'border-destructive' : ''}
            />
            {errors.notes && (
              <p className="text-sm text-destructive">{errors.notes}</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid}>
            Add Child Level
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
